/**
 * Backend calls for runtime feature flags (identity `/featureFlags`).
 *
 * Role rules are enforced server-side in every environment and computed on availability, not on
 * field values: a write that takes builds away from a key whose registry `riskyDirection` is
 * `disable` (or adds builds to one whose `riskyDirection` is `enable`) needs `superAdmin`; every
 * other write is open to any admin. Every write carries a mandatory `reason`. Updates are guarded
 * by `updatedAt` (409 `incorrectVersion` when stale). Creating a second live row for a key is a
 * 400. There is no DELETE: "back to the code default" is a write.
 */
import type { FeatureFlagMode, FeatureFlagRecord } from "@/firestore/featureFlags";
import { adminRequest } from "./client";

export interface FeatureFlagCreateInput {
  key: string;
  iosMode: FeatureFlagMode;
  iosMinBuild?: number | null;
  iosMaxBuild?: number | null;
  androidMode: FeatureFlagMode;
  androidMinBuild?: number | null;
  androidMaxBuild?: number | null;
  message?: string | null;
  expectedBackAt?: string | null;
  reason: string;
}

/** Subset of the mutable fields (`null` clears a nullable one) + the mandatory `reason`. */
export interface FeatureFlagUpdateInput {
  iosMode?: FeatureFlagMode;
  iosMinBuild?: number | null;
  iosMaxBuild?: number | null;
  androidMode?: FeatureFlagMode;
  androidMinBuild?: number | null;
  androidMaxBuild?: number | null;
  message?: string | null;
  expectedBackAt?: string | null;
  reason: string;
}

export type FeatureFlagCommand =
  | { kind: "create"; input: FeatureFlagCreateInput }
  | { kind: "update"; id: string; updatedAt: string; input: FeatureFlagUpdateInput };

/** `GET /featureFlags` — every live flag, full rows incl. `reason` / `updatedBy`. */
export async function listFeatureFlags(
  token: string,
  signal?: AbortSignal,
): Promise<FeatureFlagRecord[]> {
  const page = await adminRequest<{ items: FeatureFlagRecord[] }>({
    method: "GET",
    path: "/featureFlags",
    token,
    signal,
  });
  return page.items;
}

/** `POST /featureFlags` — create the key's row (400 if a live row already exists). */
export function createFeatureFlag(
  token: string,
  body: FeatureFlagCreateInput,
): Promise<FeatureFlagRecord> {
  return adminRequest<FeatureFlagRecord>({
    method: "POST",
    path: "/featureFlags",
    body,
    token,
  });
}

/** `PATCH /featureFlags/{id}?updatedAt=` — optimistic-concurrency update. */
export function updateFeatureFlag(
  token: string,
  id: string,
  updatedAt: string,
  body: FeatureFlagUpdateInput,
): Promise<FeatureFlagRecord> {
  return adminRequest<FeatureFlagRecord>({
    method: "PATCH",
    path: `/featureFlags/${encodeURIComponent(id)}`,
    query: { updatedAt },
    body,
    token,
  });
}
